import { useCallback, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import * as Theme from '@upstox/upstox-ui/theme';
import { useThemeDetector } from '@upstox/upstox-ui';
// modules
import PriceAlertsService from 'modules/PriceAlerts/service';
import Format from 'modules/Format';
import { useStore } from 'modules/Stores';
import Localization from 'modules/Localization';
import LiveData from 'modules/LiveData';
// typings
import { PriceAlertRule, PriceAlertType, PriceAlert } from 'modules/PriceAlerts/typings';
import { IChartingLibraryWidget, IChartWidgetApi, IOrderLineAdapter } from 'modules/TradingChart/components/TradingView/sources/charting_library.min';
import Search from 'modules/Search';

const usePriceAlertsInChart = () => {
  const priceAlertOrderLines = useRef<IOrderLineAdapter[]>([]);
  const { theme } = useStore('AppStore');
  const autoTheme = useThemeDetector();
  const currentTheme = theme === 'auto' ? autoTheme : theme;

   // Handle price alert deletion
  const onPriceAlertDelete = useCallback(
    async (id: string, exchangeToken: string) => {
      await PriceAlertsService.deletePriceAlert(id, exchangeToken);
      await PriceAlertsService.getPriceAlerts();
    },
  []);

  // Handle price alert move
  const onPriceAlertMove = useCallback(
    async (
      alert: PriceAlert,
      type: PriceAlertType,
      currentPrice: number,
      exchangeToken: string,
    ) => {
      const alertCopy = cloneDeep(alert);
      const rule = alertCopy.rules.find(item => item.type === type);
      if (!rule) return;

      rule.value = currentPrice;

      const data = await Search.searchByExchangeToken(exchangeToken);
      PriceAlertsService.setScripData(data);
      const modifiedRules = alertCopy.rules
        .filter((item) => item?.value > 0 && item.status !== 'TRIGGERED')
        .map(item => ({
          type: item.type,
          value: item.value,
          notes: item.notes,
        }));

      // delete call if no rule
      if (modifiedRules.length === 0) {
        return onPriceAlertDelete(alert.id, exchangeToken);
      }

      const editBody = {
        compareWith: 'LTP',
        rules: modifiedRules,
      };
      const ltp = LiveData
        .getByInstrumentKey(alert.instrumentKey)?.lastTradedPrice;
      await PriceAlertsService
        .updatePriceAlert(alertCopy.id, editBody, exchangeToken, Number(ltp), 'notification');
      await PriceAlertsService.getPriceAlerts();
    },
    [onPriceAlertDelete],
  );

  // Extract exchange token from the chart
  const getExchangeTokenOfChart = useCallback(
    (tvWidget: IChartingLibraryWidget, index: number) => {
      const currentSymbol = tvWidget.chart(index)?.symbolExt()?.full_name || '';
      return Format.decodeCustomTvExchangeToken(currentSymbol);
    },
    [],
  );

  // Configure an order line
  const configureOrderLine = useCallback(
    (
      orderLine: IOrderLineAdapter,
      rule: PriceAlertRule,
      alert: PriceAlert,
      exchangeToken: string,
    ) => {
      const { colors, fonts, fontWeights, fontSizes } = Theme[currentTheme];
      const defaultOptions = {
        lineColor: colors.background.yellow,
        lineStyle: 2,
        lineWidth: 1,
        backgroundColor: colors.text[3],
        borderColor: colors.background.yellow,
        textColor: colors.text.default,
        bodyFont: `${fontWeights.normal} ${fontSizes.meta}px ${fonts.tvFont}`,
      };

      priceAlertOrderLines.current.push(orderLine);

      orderLine
        .setText(Localization.translate('ALERT_ON_VALUE', rule.value.toString()))
        .setPrice(Number(rule.value))
        .setQuantity('')
        .setEditable(true)
        .setExtendLeft(true)
        .setLineStyle(defaultOptions.lineStyle)
        .setLineWidth(defaultOptions.lineWidth)
        .setLineColor(defaultOptions.lineColor)
        .setBodyBackgroundColor(defaultOptions.backgroundColor)
        .setBodyBorderColor(defaultOptions.borderColor)
        .setBodyTextColor(defaultOptions.textColor)
        .setBodyFont(defaultOptions.bodyFont)
        .setModifyTooltip(Localization.translate('DRAG_TO_MODIFY_PRICE_ALERT'))
        .setCancelTooltip(Localization.translate('CANCEL_ALERT'))
        .setCancelButtonBorderColor(defaultOptions.borderColor)
        .setCancelButtonBackgroundColor(defaultOptions.backgroundColor)
        .setCancelButtonIconColor(defaultOptions.textColor)
        .onCancel(() => {
          onPriceAlertMove(alert, rule.type, 0, exchangeToken);
        })
        .onMove(() => {
          const newPrice = orderLine.getPrice();
          orderLine.setText(Localization.translate('ALERT_ON_VALUE', newPrice.toString()));
          onPriceAlertMove(alert, rule.type, newPrice, exchangeToken);
        });
    },
    [currentTheme, onPriceAlertMove],
  );

  const createOrderLine = useCallback(
    ({ exchangeToken, chart } :
      {
        exchangeToken: string,
        chart: IChartWidgetApi
      },
    ) => {
    const { priceAlertList } = PriceAlertsService;
    const matchedAlert = priceAlertList.find(
      (alert) => `${alert.instrument.e}|${alert.instrument.t}` === exchangeToken,
    );
    if (!matchedAlert?.rules) return;
    matchedAlert.rules
      .filter((rule) => rule.status !== 'TRIGGERED')
      .forEach((rule) => {
        const orderLine = chart.createOrderLine({ disableUndo: false });
        if (orderLine) {
          configureOrderLine(orderLine, rule, matchedAlert, exchangeToken);
        }
      });
  }, [configureOrderLine]);

  const removeExistingOrderLines = useCallback(() => {
    // Remove existing order lines
    priceAlertOrderLines.current.forEach((orderLine) => orderLine.remove());
    priceAlertOrderLines.current = [];
  }, []);

  // Create price alerts on the chart
  const createPriceAlert = useCallback(
    (tvWidget: IChartingLibraryWidget) => {
      removeExistingOrderLines();
      if (!tvWidget.chartsCount()) return;
      const charts = Array.from(
        { length: tvWidget.chartsCount() }, (_, index) => ({
        exchangeToken: getExchangeTokenOfChart(tvWidget, index),
        chart: tvWidget.chart(index),
      }));
      charts.forEach(createOrderLine);
    },
    [getExchangeTokenOfChart, removeExistingOrderLines, createOrderLine],
  );

  return {
    createPriceAlert,
  };
};

export default usePriceAlertsInChart;
